import { useFormikContext } from 'formik'
import { BasicDatePicker } from '../BasicDatePicker'
import { Dayjs } from 'dayjs'
import React, { useCallback } from 'react'
import { isFinishedDatePick } from 'src/utils/datePick'

interface Props<T> {
  name: keyof T
  label: string
  /** Earliest selectable day. Optional, so existing callers are unaffected. */
  minDate?: Dayjs
  /** Latest selectable day. Optional, so existing callers are unaffected. */
  maxDate?: Dayjs
  /** Optional, so existing callers are unaffected. */
  disabled?: boolean
  /** Stretch the field to its container. Optional, so existing callers are unaffected. */
  fullWidth?: boolean
  /**
   * Touch the field and re-validate it with the NEW date as soon as a date is picked (or the field
   * cleared) — for forms that validate on blur only, where a pick would otherwise leave a stale
   * error up. A date picker reports no blur of its own, and opening its calendar steals focus
   * before anything is picked. Optional, so existing callers are unaffected.
   */
  validateOnPick?: boolean
}

export const FormikDatePicker = <T extends unknown>({
  name,
  label,
  minDate,
  maxDate,
  disabled,
  fullWidth,
  validateOnPick = false,
}: Props<T>) => {
  const { values, touched, errors, setFieldValue, setFieldTouched } = useFormikContext<T>()

  const isTouched = touched[name] as boolean
  const error = errors[name] as string | undefined

  const onChange = useCallback(
    (date: Dayjs | null) => {
      if (!validateOnPick || !isFinishedDatePick(date)) {
        setFieldValue(name as string, date)

        return
      }

      // Validate from `setFieldValue`, which checks the new value; `setFieldTouched` would
      // re-check Formik's previous values, so it is told not to validate.
      setFieldValue(name as string, date, true)
      setFieldTouched(name as string, true, false)
    },
    [name, setFieldTouched, setFieldValue, validateOnPick],
  )

  return (
    <BasicDatePicker
      name={name as string}
      onChange={onChange}
      value={values[name] as unknown as Dayjs | undefined}
      error={Boolean(isTouched && error)}
      helperText={isTouched && error}
      label={label}
      minDate={minDate}
      maxDate={maxDate}
      disabled={disabled}
      fullWidth={fullWidth}
    />
  )
}
